import { createApi } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { ontologyBaseQuery, PROXY_API_V1_URL } from '../core'
import type {
  IncidentDraft,
  OccurrenceInvestigation,
  ProblemDebugItem,
  ProblemOccurrence,
  ProblemTaskGroup,
  ProblemTaskList,
  ProblemTaskResult,
  WorkListArgs,
  WorkRecordArgs,
  WorkScope
} from './types'

type Query = (args: Parameters<typeof ontologyBaseQuery>[0]) => ReturnType<typeof ontologyBaseQuery>
const path = (suffix: string) => `${PROXY_API_V1_URL}/${suffix}`
export const workScopeKey = (scope: WorkScope): string =>
  JSON.stringify([scope.tenantId, scope.viewerId, scope.branchId])
const tags = (scope: WorkScope) => [{ type: 'Work' as const, id: workScopeKey(scope) }]
const failure = (message: string): FetchBaseQueryError => ({
  status: 'CUSTOM_ERROR',
  error: message
})

/** A failed or truncated read never becomes a successful partial list. */
export async function readWorkPages<T>(
  baseQuery: Query,
  url: string,
  key: string
): Promise<{ data: T[] } | { error: FetchBaseQueryError }> {
  const rows: T[] = []
  const seen = new Set<string>()
  let token = ''
  do {
    const params = new URLSearchParams({
      page_size: '100',
      ...(token ? { page_token: token } : {})
    })
    const result = await baseQuery(`${url}${url.includes('?') ? '&' : '?'}${params}`)
    if (result.error) return { error: result.error }
    const page = result.data as Record<string, unknown>
    if (!Array.isArray(page[key])) return { error: failure('Invalid work page') }
    rows.push(...(page[key] as T[]))
    token = typeof page.next_page_token === 'string' ? page.next_page_token : ''
    if (token && seen.has(token)) return { error: failure('Repeated work page token') }
    seen.add(token)
  } while (token)
  return { data: rows }
}

async function listURL(baseQuery: Query, name: string, scope: WorkListArgs) {
  let branchId = scope.branchId
  if (!branchId) {
    const main = await baseQuery('/branches:main')
    if (main.error) return { error: main.error }
    branchId = (main.data as { id: string }).id
  }
  const params = new URLSearchParams({ origin_branch_id: branchId })
  if (scope.entityId) params.set('entity_id', scope.entityId)
  if (scope.runId) params.set('run_id', scope.runId)
  return { url: `${path(name)}?${params}` }
}

export async function readTaskSet(
  query: Query,
  id: string
): Promise<{ data: ProblemTaskList } | { error: FetchBaseQueryError }> {
  // Cursors fence the whole task set. Restart a torn read once, never merge pages across versions.
  for (let attempt = 0; attempt < 2; attempt++) {
    let summary: ProblemTaskList | undefined
    const result = await readWorkPages<ProblemTask>(
      async (args) => {
        const page = await query(args)
        if (page.data) summary = page.data as ProblemTaskList
        return page
      },
      path(`problem-occurrences/${encodeURIComponent(id)}/tasks`),
      'tasks'
    )
    if ('data' in result && summary) return { data: { ...summary, tasks: result.data } }
    if ('error' in result) {
      const error = result.error
      if (
        attempt === 0 &&
        error.status === 409 &&
        JSON.stringify(error.data).includes('TASK_SET_CHANGED')
      )
        continue
      return { error }
    }
  }
  return { error: failure('Task set changed. Refresh and try again.') }
}

const newerOccurrence = (old: ProblemOccurrence | undefined, next: ProblemOccurrence) =>
  old && old.version > next.version ? old : next
export function mergeWorkGroups(
  old: ProblemTaskGroup[],
  incoming: ProblemTaskGroup[]
): ProblemTaskGroup[] {
  const previous = new Map(old.map((group) => [group.occurrence.id, group]))
  // Missing IDs are evicted, including after access revocation.
  return incoming.map((group) => {
    const cached = previous.get(group.occurrence.id)
    return {
      ...(cached && cached.task_set_version > group.task_set_version ? cached : group),
      occurrence: newerOccurrence(cached?.occurrence, group.occurrence)
    }
  })
}
export const problemWorkApi = createApi({
  reducerPath: 'problemWorkApi',
  baseQuery: ontologyBaseQuery,
  tagTypes: ['Work', 'OccurrenceRuns'],
  refetchOnFocus: true,
  refetchOnReconnect: true,
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    listProblemOccurrences: builder.query<ProblemOccurrence[], WorkListArgs>({
      async queryFn(scope, _api, _extra, query) {
        const result = await listURL(query, 'problem-occurrences', scope)
        return result.error
          ? { error: result.error }
          : readWorkPages(query, result.url!, 'occurrences')
      },
      merge: (old, next) =>
        next.map((item) =>
          newerOccurrence(
            old.find((row) => row.id === item.id),
            item
          )
        ),
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    listProblemTaskGroups: builder.query<ProblemTaskGroup[], WorkListArgs>({
      async queryFn(scope, _api, _extra, query) {
        const result = await listURL(query, 'problem-task-groups', scope)
        return result.error ? { error: result.error } : readWorkPages(query, result.url!, 'groups')
      },
      merge: mergeWorkGroups,
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    listOpenProblemTasks: builder.query<ProblemTask[], WorkScope>({
      async queryFn(scope, _api, _extra, query) {
        const url = await listURL(query, 'problem-task-groups', scope)
        if (url.error) return { error: url.error }
        const groups = await readWorkPages<ProblemTaskGroup>(query, url.url!, 'groups')
        if ('error' in groups) return groups
        const tasks: ProblemTask[] = []
        for (const group of groups.data) {
          if (!group.counts.open) continue
          const result = await readTaskSet(query, group.occurrence.id)
          if ('error' in result) return result
          tasks.push(
            ...result.data.tasks.filter(
              (task) =>
                !task.retired_at &&
                task.approval_status !== 'rejected' &&
                task.work_status === 'open'
            )
          )
        }
        return { data: tasks }
      },
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    getOccurrenceDetector: builder.query<
      { target: { config_id: string; trigger_occurrence_id: string } | null },
      WorkRecordArgs
    >({
      query: ({ id }) => path(`problem-occurrences/${encodeURIComponent(id)}/detector`),
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    getProblemTask: builder.query<ProblemTask, WorkRecordArgs>({
      query: ({ id }) => path(`problem-tasks/${encodeURIComponent(id)}`),
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    getProblemOccurrence: builder.query<ProblemOccurrence, WorkRecordArgs>({
      query: ({ id }) => path(`problem-occurrences/${encodeURIComponent(id)}`),
      merge: newerOccurrence,
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    listOccurrenceInvestigations: builder.query<OccurrenceInvestigation[], WorkRecordArgs>({
      queryFn: ({ id }, _api, _extra, query) =>
        readWorkPages(
          query,
          path(`problem-occurrences/${encodeURIComponent(id)}/investigations`),
          'investigations'
        ),
      providesTags: (_data, _error, scope) => [
        ...tags(scope),
        { type: 'OccurrenceRuns', id: workScopeKey(scope) }
      ]
    }),
    listIncidentDrafts: builder.query<IncidentDraft[], WorkScope & { runId?: string }>({
      query: ({ runId }) =>
        path(`custom-agents/incident-drafts${runId ? `?run_id=${encodeURIComponent(runId)}` : ''}`),
      transformResponse: (response: { drafts?: IncidentDraft[] }) => response.drafts ?? [],
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    listOccurrenceDebugItems: builder.query<ProblemDebugItem[], WorkRecordArgs>({
      queryFn: ({ id }, _api, _extra, query) =>
        readWorkPages(
          query,
          path(`problem-occurrences/${encodeURIComponent(id)}/debug-items`),
          'items'
        ),
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    listProblemTasks: builder.query<ProblemTaskList, WorkRecordArgs>({
      async queryFn({ id }, _api, _extra, query) {
        return readTaskSet(query, id)
      },
      merge: (old, next) => (old.task_set_version > next.task_set_version ? old : next),
      providesTags: (_data, _error, scope) => tags(scope)
    }),
    setProblemOccurrenceStatus: builder.mutation<
      ProblemOccurrence,
      WorkRecordArgs & { action: 'close' | 'reopen'; expectedVersion: number; operationKey: string }
    >({
      query: ({ id, action, expectedVersion, operationKey }) => ({
        url: path(`problem-occurrences/${encodeURIComponent(id)}:${action}`),
        method: 'POST',
        body: {
          operation_key: operationKey,
          expected_version: expectedVersion,
          ...(action === 'close' ? { reason: 'Closed from Problems' } : {})
        }
      }),
      async onQueryStarted(scope, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          for (const args of problemWorkApi.util.selectCachedArgsForQuery(
            getState(),
            'listProblemOccurrences'
          )) {
            if (workScopeKey(args) !== workScopeKey(scope)) continue
            dispatch(
              problemWorkApi.util.updateQueryData('listProblemOccurrences', args, (rows) =>
                rows.map((row) => (row.id === data.id ? newerOccurrence(row, data) : row))
              )
            )
          }
          for (const args of problemWorkApi.util.selectCachedArgsForQuery(
            getState(),
            'listProblemTaskGroups'
          )) {
            if (workScopeKey(args) !== workScopeKey(scope)) continue
            dispatch(
              problemWorkApi.util.updateQueryData('listProblemTaskGroups', args, (rows) => {
                const group = rows.find((row) => row.occurrence.id === data.id)
                if (group) group.occurrence = newerOccurrence(group.occurrence, data)
              })
            )
          }
        } catch {
          /* The caller presents the error; invalidation refreshes conflicts. */
        }
      },
      invalidatesTags: (_data, _error, scope) => tags(scope)
    }),
    setProblemTasksComplete: builder.mutation<
      ProblemTaskResult,
      WorkRecordArgs & {
        action: 'complete' | 'restore'
        taskId?: string
        expectedVersion: number
        operationKey: string
      }
    >({
      query: ({ id, taskId, action, expectedVersion, operationKey }) => ({
        url: path(
          taskId
            ? `problem-tasks/${encodeURIComponent(taskId)}:${action}`
            : `problem-occurrences/${encodeURIComponent(id)}/tasks:${action}`
        ),
        method: 'POST',
        body: taskId
          ? { operation_key: operationKey, expected_version: expectedVersion, occurrence_id: id }
          : { operation_key: operationKey, expected_task_set_version: expectedVersion }
      }),
      async onQueryStarted(scope, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const {
            taskId: _taskId,
            action,
            expectedVersion: _version,
            operationKey: _key,
            ...record
          } = scope
          dispatch(
            problemWorkApi.util.updateQueryData('listProblemTasks', record, (list) => {
              if (list.task_set_version > data.task_set_version) return
              list.task_set_version = data.task_set_version
              list.counts = data.counts
              for (const changed of data.changed_tasks ?? []) {
                const task = list.tasks.find((task) => task.id === changed.id)
                if (task && task.version <= changed.version) {
                  task.version = changed.version
                  task.work_status = action === 'complete' ? 'done' : 'open'
                }
              }
            })
          )
          for (const args of problemWorkApi.util.selectCachedArgsForQuery(
            getState(),
            'listProblemTaskGroups'
          )) {
            if (workScopeKey(args) !== workScopeKey(scope)) continue
            dispatch(
              problemWorkApi.util.updateQueryData('listProblemTaskGroups', args, (groups) => {
                const group = groups.find((group) => group.occurrence.id === data.occurrence_id)
                if (group && group.task_set_version <= data.task_set_version) {
                  group.task_set_version = data.task_set_version
                  group.counts = data.counts
                }
              })
            )
          }
        } catch {
          /* Refetch conflicts; never write through the legacy adapter. */
        }
      },
      invalidatesTags: (_data, _error, scope) => tags(scope)
    })
  })
})
import type { ProblemTask } from './types'
export const {
  useListProblemOccurrencesQuery,
  useGetProblemTaskQuery,
  useGetOccurrenceDetectorQuery,
  useListOpenProblemTasksQuery,
  useListProblemTaskGroupsQuery,
  useGetProblemOccurrenceQuery,
  useListOccurrenceInvestigationsQuery,
  useListOccurrenceDebugItemsQuery,
  useListIncidentDraftsQuery,
  useListProblemTasksQuery,
  useSetProblemOccurrenceStatusMutation,
  useSetProblemTasksCompleteMutation
} = problemWorkApi
